import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Navigate } from "react-router-dom";

import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Loader2, Database } from "lucide-react";
import logoMindsup from "@/assets/logo-mindsup.svg";
import {
  fetchBootstrapStatus,
  postBootstrapImport,
  extractMediaUrls,
  postMigrateMediaFile,
  postRewriteMediaUrls,
  formatMediaError,
  type BackupPayload,
  type ImportBackupResult,
} from "@/lib/backupApi";

export default function Login() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();

  const [bootstrapPhase, setBootstrapPhase] = useState<"loading" | "empty" | "has_users" | "unknown">("loading");
  const [bootstrapCheckError, setBootstrapCheckError] = useState<string | null>(null);
  const [bootstrapImporting, setBootstrapImporting] = useState(false);
  const [bootstrapProgress, setBootstrapProgress] = useState(0);
  const [bootstrapResult, setBootstrapResult] = useState<ImportBackupResult | null>(null);

  // Media migration state
  const [migratingMedia, setMigratingMedia] = useState(false);
  const [mediaTotal, setMediaTotal] = useState(0);
  const [mediaDone, setMediaDone] = useState(0);
  const [mediaUploaded, setMediaUploaded] = useState(0);
  const [mediaSkipped, setMediaSkipped] = useState(0);
  const [mediaErrors, setMediaErrors] = useState<string[]>([]);

  const bootstrapSecret =
    typeof import.meta.env.VITE_BACKUP_BOOTSTRAP_SECRET === "string" &&
    import.meta.env.VITE_BACKUP_BOOTSTRAP_SECRET.length > 0
      ? import.meta.env.VITE_BACKUP_BOOTSTRAP_SECRET
      : undefined;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBootstrapPhase("loading");
      setBootstrapCheckError(null);
      try {
        const data = await fetchBootstrapStatus(bootstrapSecret);
        if (cancelled) return;
        setBootstrapPhase(data.allowFullImport ? "empty" : "has_users");
      } catch (e) {
        if (cancelled) return;
        setBootstrapPhase("unknown");
        setBootstrapCheckError(e instanceof Error ? e.message : "Erreur réseau ou fonction indisponible");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bootstrapSecret]);

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (user && isAdmin) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { error } = await signIn(email, password);
    if (error) {
      setError("Identifiants invalides ou accès non autorisé.");
    }
    setLoading(false);
  };

  const runBootstrapMediaMigration = async (payload: BackupPayload) => {
    const urls = extractMediaUrls(payload);
    if (urls.length === 0) return;

    setMigratingMedia(true);
    setMediaTotal(urls.length);
    setMediaDone(0);
    setMediaUploaded(0);
    setMediaSkipped(0);
    setMediaErrors([]);

    let uploaded = 0;
    let skipped = 0;
    const errors: string[] = [];
    const replacements: { oldUrl: string; newUrl: string }[] = [];

    for (const entry of urls) {
      try {
        const result = await postMigrateMediaFile(null, entry, bootstrapSecret);
        if (result.ok && result.newUrl) {
          replacements.push({ oldUrl: entry.originalUrl, newUrl: result.newUrl });
          if (result.status === "uploaded") uploaded++;
          else skipped++;
        } else {
          errors.push(formatMediaError(result.error || `Erreur ${entry.originalUrl}`, entry));
        }
      } catch (e) {
        errors.push(formatMediaError(e instanceof Error ? e.message : `Erreur ${entry.originalUrl}`, entry));
      }
      setMediaDone((prev) => prev + 1);
      setMediaUploaded(uploaded);
      setMediaSkipped(skipped);
      setMediaErrors([...errors]);
    }

    // Rewrite URLs in database
    if (replacements.length > 0) {
      try {
        const rewriteResult = await postRewriteMediaUrls(null, replacements, bootstrapSecret);
        if (rewriteResult.ok) {
          console.log(`[media-rewrite] ${rewriteResult.updated_rows} row(s) updated in DB`);
          if (rewriteResult.warnings?.length) {
            errors.push(...rewriteResult.warnings);
          }
        } else {
          errors.push(`Réécriture URLs en BDD : ${rewriteResult.error}`);
        }
      } catch (e) {
        errors.push(`Réécriture URLs en BDD : ${e instanceof Error ? e.message : String(e)}`);
      }
      setMediaErrors([...errors]);
    }

    setMigratingMedia(false);
    if (errors.length > 0) {
      toast.warning(`Migration médias : ${uploaded} uploadé(s), ${skipped} ignoré(s), ${errors.length} erreur(s)`);
    } else {
      toast.success(`Migration médias terminée : ${uploaded} uploadé(s), ${skipped} déjà présent(s)`);
    }
  };

  const handleBootstrapFile = async (file: File | null) => {
    if (!file) return;
    setBootstrapImporting(true);
    setBootstrapProgress(10);
    setBootstrapResult(null);
    const progressInterval = setInterval(() => {
      setBootstrapProgress((prev) => (prev >= 90 ? 90 : prev + Math.random() * 8));
    }, 800);
    try {
      const text = await file.text();
      const payload = JSON.parse(text) as BackupPayload;
      // Always skip media in the main import — migrate file by file after
      const result = await postBootstrapImport(payload, bootstrapSecret, {
        skipMediaMigration: true,
      });
      clearInterval(progressInterval);
      setBootstrapProgress(100);
      setBootstrapResult(result);
      if (!result.ok) {
        toast.error(result.errors?.join(" ; ") || "Import échoué");
        return;
      }
      toast.success("Base restaurée. Migration des médias en cours…");

      // Run media migration file by file
      await runBootstrapMediaMigration(payload);
    } catch (e) {
      clearInterval(progressInterval);
      const msg =
        e instanceof SyntaxError
          ? `Fichier JSON invalide : ${e.message}`
          : e instanceof Error
            ? e.message
            : "Erreur lors de l'import";
      toast.error(msg);
    } finally {
      setTimeout(() => {
        setBootstrapImporting(false);
        setBootstrapProgress(0);
      }, 600);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center gap-3">
          <img src={logoMindsup} alt="MindsUp" className="h-12" />
          <p className="text-sm text-muted-foreground">Espace d'administration</p>
        </div>

        {(bootstrapPhase === "empty" || bootstrapPhase === "unknown") ? (
          <Card className="border-amber-500/40 shadow-lg">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Database className="h-4 w-4" />
                Restauration JSON (première installation)
              </CardTitle>
              {bootstrapPhase === "empty" && (
                <CardDescription>
                  Aucun utilisateur Auth détecté. Vous pouvez importer un fichier JSON de sauvegarde complet ; tout le
                  contenu du fichier sera restauré. Les fichiers médias seront migrés un par un après l'import.
                </CardDescription>
              )}
              {bootstrapPhase === "unknown" && (
                <CardDescription>
                  Impossible de vérifier l'état du serveur ({bootstrapCheckError ?? "erreur inconnue"}). Vous pouvez
                  quand même sélectionner un fichier : l'import ne sera accepté que si la base Auth est vraiment vide.
                  Vérifiez que l'Edge Function <code className="text-xs">backup</code> est déployée et que{" "}
                  <code className="text-xs">VITE_SUPABASE_URL</code> pointe vers votre projet. Si{" "}
                  <code className="text-xs">BACKUP_BOOTSTRAP_SECRET</code> est défini côté serveur, renseignez aussi{" "}
                  <code className="text-xs">VITE_BACKUP_BOOTSTRAP_SECRET</code> dans l'admin.
                </CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              <input
                type="file"
                accept="application/json,.json"
                disabled={bootstrapImporting || migratingMedia}
                className="text-sm"
                onChange={(e) => handleBootstrapFile(e.target.files?.[0] ?? null)}
              />
              {bootstrapImporting && (
                <div className="space-y-1">
                  <Progress value={bootstrapProgress} className="h-2" />
                  <p className="text-xs text-muted-foreground">Import des données en cours…</p>
                </div>
              )}
              {migratingMedia && (
                <div className="space-y-1">
                  <Progress value={mediaTotal > 0 ? (mediaDone / mediaTotal) * 100 : 0} className="h-2" />
                  <p className="text-xs text-muted-foreground">
                    Migration des médias : {mediaDone}/{mediaTotal} — {mediaUploaded} uploadé(s), {mediaSkipped} ignoré(s)
                    {mediaErrors.length > 0 && `, ${mediaErrors.length} erreur(s)`}
                  </p>
                </div>
              )}
              {!migratingMedia && mediaErrors.length > 0 && (
                <Alert variant="destructive">
                  <AlertTitle>Erreurs de migration médias</AlertTitle>
                  <AlertDescription className="mt-1 max-h-32 overflow-auto text-xs whitespace-pre-wrap">
                    {mediaErrors.join("\n")}
                  </AlertDescription>
                </Alert>
              )}
              {bootstrapResult?.ok && bootstrapResult.generated_credentials?.length ? (
                <Alert>
                  <AlertTitle>Mots de passe provisoires</AlertTitle>
                  <AlertDescription className="mt-2 max-h-48 overflow-auto font-mono text-xs whitespace-pre-wrap">
                    {bootstrapResult.generated_credentials
                      .map((c) => `${c.email} → ${c.temporary_password}`)
                      .join("\n")}
                  </AlertDescription>
                </Alert>
              ) : null}
            </CardContent>
          </Card>
        ) : null}

        <Card className="border-border/50 shadow-lg">
          <CardHeader className="pb-4">
            <h2 className="text-lg font-semibold text-center" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Connexion
            </h2>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@mindsup.app"
                  required
                  autoComplete="email"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Mot de passe</Label>
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline"
                    onClick={async () => {
                      if (!email.trim()) {
                        toast.error("Veuillez d'abord saisir votre email.");
                        return;
                      }
                      try {
                        const res = await fetch(
                          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-password-email`,
                          {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ email: email.trim() }),
                          }
                        );
                        const result = await res.json();
                        if (!res.ok) throw new Error(result.error || "Erreur");
                        toast.success("Un email de réinitialisation a été envoyé.");
                      } catch {
                        toast.error("Erreur lors de l'envoi du mail.");
                      }
                    }}
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Se connecter
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="text-center space-y-1">
          <p className="text-xs text-muted-foreground">Accès réservé aux administrateurs</p>
          <div className="flex items-center justify-center gap-3 text-xs">
            <a
              href="/legal/cgu"
              className="text-muted-foreground hover:text-primary hover:underline transition-colors"
            >
              CGU
            </a>
            <span className="text-muted-foreground/40">·</span>
            <a
              href="/legal/privacy"
              className="text-muted-foreground hover:text-primary hover:underline transition-colors"
            >
              Confidentialité
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
